import { useState } from 'react';
import { useScrollAnimation, getAnimationClass } from '../../../hooks/useScrollAnimation';

function AnimatedSection({ children, animation = 'slide-up' as 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in', className = '' }: { children: React.ReactNode; animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in'; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`${getAnimationClass(isVisible, animation)} ${className}`}>
      {children}
    </div>
  );
}

export default function DemoForm() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    clinica: '',
    especialidad: 'Oftalmología',
    mensaje: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nombre || !formData.email) return;

    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('nombre', formData.nombre);
      body.append('email', formData.email);
      body.append('telefono', formData.telefono);
      body.append('clinica', formData.clinica);
      body.append('especialidad', formData.especialidad);
      body.append('mensaje', formData.mensaje);

      await fetch('https://readdy.ai/api/form/d6acadea728k8ctu387g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="demo" className="py-24 bg-gray-950 relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <AnimatedSection animation="slide-left">
            <div className="space-y-8">
              <span className="inline-block text-xs font-semibold text-emerald-400 uppercase tracking-widest">Registrarse / Ingresar</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
                Transforma tu práctica oftalmológica <span className="font-display italic text-emerald-400">hoy</span>
              </h2>
              <p className="text-sm text-white/70 leading-relaxed">
                Agenda una demostración personalizada y descubre cómo Anamnesy Soft puede optimizar tu consulta. Sin compromiso, sin costo.
              </p>

              <div className="space-y-5 pt-4">
                {[
                  { icon: 'ri-time-line', text: 'Demo personalizada de 30 minutos' },
                  { icon: 'ri-settings-3-line', text: 'Configuración adaptada a tu consulta' },
                  { icon: 'ri-gift-line', text: 'Prueba gratuita de 14 días incluida' },
                  { icon: 'ri-customer-service-2-line', text: 'Soporte dedicado durante la prueba' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-emerald-400 text-sm`}></i>
                    </div>
                    <span className="text-sm text-white/80">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* Right - Form */}
          <AnimatedSection animation="slide-right">
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              {submitted ? (
                <div className="text-center py-12 space-y-4">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                    <i className="ri-check-line text-emerald-600 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">¡Solicitud enviada!</h3>
                  <p className="text-sm text-gray-500">Nuestro equipo se pondrá en contacto contigo en las próximas 24 horas para agendar tu demo.</p>
                </div>
              ) : (
                <form id="demo-request-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Solicita tu demo gratuita</h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Nombre completo *</label>
                      <input
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        required
                        placeholder="Dr. Juan Pérez"
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="doctor@clinica.com"
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Teléfono</label>
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        placeholder="+1 (555) 123-4567"
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Clínica / Consultorio</label>
                      <input
                        type="text"
                        name="clinica"
                        value={formData.clinica}
                        onChange={handleChange}
                        placeholder="Clínica Visual"
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Especialidad</label>
                    <select
                      name="especialidad"
                      value={formData.especialidad}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors cursor-pointer bg-white"
                    >
                      <option value="Oftalmología">Oftalmología</option>
                      <option value="Optometría">Optometría</option>
                      <option value="Óptica">Óptica</option>
                      <option value="Otra">Otra especialidad</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Mensaje (opcional)</label>
                    <textarea
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleChange}
                      maxLength={500}
                      rows={3}
                      placeholder="Cuéntanos sobre tu consulta..."
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                    ></textarea>
                    <p className="text-[10px] text-gray-400 mt-1 text-right">{formData.mensaje.length}/500</p>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 bg-emerald-500 text-white text-sm font-semibold rounded-lg hover:bg-emerald-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? 'Enviando...' : 'Registrarse / Ingresar'}
                  </button>

                  <p className="text-[11px] text-gray-400 text-center">
                    Al enviar, aceptas nuestra política de privacidad. No compartimos tus datos.
                  </p>
                </form>
              )}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
